import { sumBy } from 'lodash'

import { InstrumentBookFullDto } from '@/gen/marketDataApi'

import { TableCell, TableRow } from '@/components/table'

import { cn } from '@/utils/cn'

type Points = Array<(InstrumentBookFullDto['bids'][0] | InstrumentBookFullDto['asks'][0]) & { quantity: number }>

interface Props {
    points: Points
    bidOrAsk: 'bid' | 'ask'
    largestQuantity: number
    otherSideCount: number
}

const headers = {
    bid: {
        graphColor: 'bg-positive-bar',
    },
    ask: {
        graphColor: 'bg-negative-bar',
    },
}

export default function DepthPricePoints({ points, bidOrAsk, largestQuantity, otherSideCount }: Props) {
    const isBid = bidOrAsk === 'bid'
    const totalQuantity = sumBy(points, 'quantity')
    const totalVolume = sumBy(points, 'orderVolumes.length')

    // Figure out if we need to add any empty rows to make the totals on either side
    // line up
    const emptyRows = otherSideCount > points.length ? otherSideCount - points.length : 0
    const tableRowClassName = cn('hover:bg-secondary', {
        'text-right': isBid,
        'text-left': !isBid,
    })

    return (
        <>
            {points.map((point, i) => {
                const graphPercentage = (point.quantity / largestQuantity) * 100
                const alignment = isBid ? 'left' : 'right'
                const ordersCmp = <div>{point.orderVolumes.length}</div>
                const priceCmp = <div>${point.price}</div>
                const quantityCmp = (
                    <div className="relative">
                        <span
                            className={cn('absolute bottom-0 top-0 z-0 bg-muted', headers[bidOrAsk].graphColor, {
                                ['right-0']: isBid,
                                ['left-0']: !isBid,
                            })}
                            style={{
                                [alignment]: `${100 - graphPercentage}%`,
                            }}
                        />
                        <span className="relative z-10">{point.quantity}</span>
                    </div>
                )

                return (
                    <TableRow key={`${point.price}-${point.quantity}-${i}`} className={tableRowClassName}>
                        <TableCell> {isBid ? ordersCmp : priceCmp}</TableCell>
                        <TableCell>{quantityCmp}</TableCell>
                        <TableCell>{isBid ? priceCmp : ordersCmp}</TableCell>
                    </TableRow>
                )
            })}
            {[...Array(emptyRows)].map((_, i) => (
                <TableRow key={i} className="border-b-transparent">
                    <TableCell>&nbsp;</TableCell>
                </TableRow>
            ))}
            <TableRow className={cn('border-t-2 border-b-transparent border-t-foreground', tableRowClassName)}>
                <TableCell>{isBid ? totalVolume : ''}</TableCell>
                <TableCell>{totalQuantity}</TableCell>
                <TableCell>{isBid ? '' : totalVolume}</TableCell>
            </TableRow>
        </>
    )
}
